import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Spinner, SpinnerSize } from "@blueprintjs/core";

import { ApiError, authRequestRaw, checkStatus } from "@api";
import { AppToaster } from "@components/AppToaster";

interface Props {
  url: string;
  filename: string;
  headers?: HeadersInit;
}

const TextLink = styled.a`
  color: inherit;
  text-decoration: none;
  :hover {
    color: inherit;
    text-decoration: none;
  }
`;

// Source: https://blog.mellisdesigns.com/react-authenticated-file-downloads/
export const AuthenticatedLink: React.FC<React.PropsWithChildren<Props>> = ({
  url,
  filename,
  children,
  headers,
}) => {
  const [isFetching, setIsFetching] = useState(false);
  const link = useRef<HTMLAnchorElement>(null);

  const handleAction = async () => {
    if (link.current) {
      if (link.current.href || isFetching) {
        return;
      }

      setIsFetching(true);

      try {
        const result = await authRequestRaw<any>(
          "get",
          url,
          undefined,
          headers
        ).then(checkStatus);

        const blob = await result.blob();

        const href = window.URL.createObjectURL(blob);
        link.current.download = filename;
        link.current.href = href;
        setIsFetching(false);

        link.current.click();
      } catch (err) {
        const error = err as ApiError;
        AppToaster.show({
          message: `Could not download policy: ${error.body?.error}`,
          intent: "danger",
        });
        setIsFetching(false);
        return;
      }
    }
  };

  // When we select a new policy we want the spinner to appear again
  useEffect(() => {
    setIsFetching(false);
  }, [url]);

  return (
    /* eslint-disable-next-line jsx-a11y/anchor-is-valid */
    <TextLink ref={link} onClick={handleAction}>
      {isFetching ? (
        <Spinner intent="primary" size={SpinnerSize.SMALL} />
      ) : (
        children
      )}
    </TextLink>
  );
};
